import { ReactNode, useState } from "react";
import { Link2 } from "lucide-react";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useHtzReportAssignAhuMutation } from "@/services/backend/htz/report/api/report-api";
import { Report } from "@/services/backend/htz/report/types/report";
import { useErrValidationFailedContext } from "@/shared/lib/errors/err-validation-failed-context";
import { useHtzAirHandlingUnitListQuery } from "@/services/backend/htz/ahu/api/ahu-api";
import { AirHandlingUnit } from "@/services/backend/htz/ahu/types/air-handling-unit";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@/shared/components/ui/field";

/**
 * AssignAhuForReportDialog assigns an air handling unit to a report that has
 * none yet. Only air handling units of the report's customer and service
 * object can be picked.
 *
 * The backend creates a copy of the air handling unit data owned by the
 * report, prefilled from the most recently edited data of the air handling
 * unit (blank if none exists).
 */
export function AssignAhuForReportDialog({
  report,
  trigger,
  disabled,
  onAssigned,
}: {
  report: Report;
  trigger?: ReactNode;
  disabled?: boolean;
  onAssigned?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [ahuId, setAhuId] = useState<UUID>("");
  const [assign, { isLoading, error, reset }] = useHtzReportAssignAhuMutation();
  const { resetField } = useErrValidationFailedContext();

  const customerId = report.customerId;
  const serviceObjectId = report.serviceObjectId;
  const {
    data: airHandlingUnits,
    isLoading: listIsLoading,
    error: listError,
  } = useHtzAirHandlingUnitListQuery(
    {
      customer: { active: true, values: [customerId ?? ""] },
      serviceObject: { active: true, values: [serviceObjectId ?? ""] },
      orderBy: [{ field: "name" }],
    },
    { skip: !customerId || !serviceObjectId },
  );

  const onConfirm = () => {
    if (isLoading || ahuId === "") {
      return;
    }

    assign({ id: report.id, ahuId })
      .unwrap()
      .then(() => {
        toast.success(t("RLT-Anlage zugewiesen"));
        resetField("airHandlingUnitId");
        setAhuId("");
        reset();
        setOpen(false);
        onAssigned?.();
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  const items = airHandlingUnits?.items ?? [];
  const isEmpty = !listIsLoading && !listError && items.length === 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger || (
        <CommonDialogTrigger
          icon={Link2}
          label={t("RLT-Anlage zuweisen")}
          size="sm"
          disabled={disabled}
        />
      )}
      <DialogContent>
        <CommonDialogHeader
          title={t("RLT-Anlage zuweisen")}
          description={t(
            "Für den Anlagenbericht wird eine eigene Kopie der Anlagendaten angelegt. Sie wird aus den zuletzt bearbeiteten Anlagendaten der RLT-Anlage übernommen oder bleibt leer, wenn es noch keine gibt. Die Zuweisung kann nicht geändert werden.",
          )}
        />
        <Field>
          <FieldLabel>{t("RLT-Anlage")}</FieldLabel>
          <Select value={ahuId} onValueChange={setAhuId} disabled={isEmpty}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("Bitte wählen")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t("RLT-Anlagen")}</SelectLabel>
                {items.map((ahu) => (
                  <SelectItem key={ahu.id} value={ahu.id}>
                    {ahuLabel(ahu)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FieldDescription>
            {isEmpty
              ? t(
                  "Für den Kunden und das Serviceobjekt des Anlagenberichts sind keine RLT-Anlagen angelegt.",
                )
              : t(
                  "Es werden nur RLT-Anlagen des Kunden und Serviceobjekts des Anlagenberichts angezeigt.",
                )}
          </FieldDescription>
        </Field>
        {listError ? <RTKQueryErrorAlert error={listError} /> : null}
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Zuweisen")}
          disabled={ahuId === ""}
        />
      </DialogContent>
    </Dialog>
  );
}

function ahuLabel(ahu: AirHandlingUnit) {
  if (ahu.designation && ahu.designation !== ahu.name) {
    return `${ahu.name} · ${ahu.designation}`;
  }
  return ahu.name;
}
